'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { Illustration, IllustrationStyle, IllustrationFormat, StoryAnalysisResult, CharacterDecision } from '@/contexts/illustrations/types';

export async function createIllustration(input: {
  storyId: string;
  title: string;
  style: string;
  format: string;
  storyAnalysis: StoryAnalysisResult;
  characterDecisions: Array<{
    extractedName: string;
    action: string;
    matchedCharacterId?: string;
    createData?: { name: string; description?: string };
    skipConfirmed?: boolean;
  }>;
}): Promise<ActionResult<{ book: Illustration }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    const { IllustrationService } = await import('@/contexts/illustrations/services/BookService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getStoryRepository } = await import('@/contexts/stories/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');

    const illustrationService = new IllustrationService(getIllustrationRepository(), getStoryRepository(), getCharacterRepository());

    const decisions: CharacterDecision[] = input.characterDecisions.map(d => ({
      extractedName: d.extractedName,
      action: d.action as CharacterDecision['action'],
      matchedCharacterId: d.matchedCharacterId || undefined,
      createData: d.createData || undefined,
      skipConfirmed: d.skipConfirmed || undefined,
    }));

    const result = await illustrationService.createIllustration({
      familyId,
      storyId: input.storyId,
      title: input.title,
      style: input.style as IllustrationStyle,
      format: input.format as IllustrationFormat,
      storyAnalysis: input.storyAnalysis,
      characterDecisions: decisions,
      createdBy: userId,
    });

    if (!result.success || !result.book) {
      return { success: false, error: result.error || 'BOOK_CREATION_FAILED' };
    }

    return { success: true, data: { book: result.book } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('createBook', e) };
  }
}
